import { ClubGrowthRow } from '../../utils/clubGrowthMetrics';

export interface PulseRow extends ClubGrowthRow {}

export type MetricKey = 'salesGrowth' | 'invoiceGrowth' | 'priceGrowth' | 'basketGrowth';
export type MetricKind = 'growth' | 'level';
export type ComparisonStatus = 'better' | 'equal' | 'worse';

export interface MetricConfig {
  key: MetricKey;
  label: string;
  shortLabel: string;
  kind: MetricKind;
  sourceField: keyof Pick<PulseRow, 'invoiceCount' | 'totalAmount' | 'avgItemPrice' | 'avgItemCount'>;
  tooltip: string;
}

export const METRICS: readonly MetricConfig[] = [
  {
    key: 'invoiceGrowth',
    label: 'رشد تعداد سفارش',
    shortLabel: 'سفارش',
    kind: 'growth',
    sourceField: 'invoiceCount',
    tooltip: 'مقایسه تغییر درصد تعداد سفارش‌های ماه‌به‌ماه شعبه با کل بازار.',
  },
  {
    key: 'salesGrowth',
    label: 'رشد میزان فروش',
    shortLabel: 'فروش',
    kind: 'growth',
    sourceField: 'totalAmount',
    tooltip: 'مقایسه تغییر درصد میزان فروش ماه‌به‌ماه شعبه با کل  کسب‌وکارهای متصل بازار .',
  },
  
  {
    key: 'priceGrowth',
    label: 'رشد قیمت (میانگین قیمت منو)',
    shortLabel: 'قیمت',
    kind: 'growth',
    sourceField: 'avgItemPrice',
    tooltip: 'مقایسه تغییر درصد میانگین قیمت منو (میانگین قیمت هر واحد کالا) شعبه با کل بازار.',
  },
  {
    key: 'basketGrowth',
    label: 'رشد سبد',
    shortLabel: 'سبد',
    kind: 'growth',
    sourceField: 'avgItemCount',
    tooltip: 'هر مشتری به طور متوسط چند قلم کالا در یک سفارش خریده است — تغییر درصد نسبت به ماه قبل.',
  },
];

/** ترتیب نمایش کارت‌های KPI (راست به چپ در RTL) */
export const KPI_METRIC_ORDER: MetricKey[] = [
  'salesGrowth',
  'invoiceGrowth',
  'priceGrowth',
  'basketGrowth',
];

/** عنوان کوتاه کارت KPI — مطابق mockup ارائه */
export const KPI_CARD_LABELS: Record<MetricKey, string> = {
  salesGrowth: 'رشد فروش',
  invoiceGrowth: 'رشد تعداد فاکتور',
  priceGrowth: 'رشد قیمت (میانگین قیمت منو)',
  basketGrowth: 'رشد سبد خرید',
};

export const TOLERANCE_PP = 1;

// ---------------------------------------------------------------------------
// شناسه‌ها
// ---------------------------------------------------------------------------
export const MARKET_PULSE_DASHBOARD_SOURCE = 'marketPulse';
export const MARKET_PULSE_KPI_ID = 'market-pulse-kpi';
export const MARKET_PULSE_TABLE_ID = 'market-pulse-table';
export const MARKET_PULSE_YOY_TABLE_ID = 'market-pulse-yoy-table';
export const MARKET_PULSE_TREND_ID = 'market-pulse-trend';

/** تعداد ماه‌هایی که در UI نبض بازار نمایش داده می‌شود (جدول، روند، انتخابگر KPI) */
export const MARKET_PULSE_DISPLAY_MONTHS = 13;

/** یک ماه اضافه برای محاسبهٔ MoM قدیمی‌ترین ماهِ نمایش‌داده‌شده */
export const MARKET_PULSE_MOM_PAD_MONTHS = 1;

/** نگاه به عقب برای رشد سال‌به‌سال (ماه متناظر سال قبل) */
export const MARKET_PULSE_YOY_LOOKBACK_MONTHS = 12;

/**
 * تعداد ماه تاریخچه برای fetch:
 * display + MoM pad + YoY lookback → ۱۳ + ۱ + ۱۲ = ۲۶
 */
export const MARKET_PULSE_FETCH_MONTHS =
  MARKET_PULSE_DISPLAY_MONTHS + MARKET_PULSE_MOM_PAD_MONTHS + MARKET_PULSE_YOY_LOOKBACK_MONTHS;

/** حداقل تعداد ماه داده برای نمایش جدول رشد سال‌به‌سال (ماه جاری + ۱۲ ماه قبل) */
export const MARKET_PULSE_YOY_MIN_MONTHS = 13;

// ---------------------------------------------------------------------------
// برچسب‌های مشترک
// ---------------------------------------------------------------------------
export const MARKET_PULSE_LABELS = {
  branch: 'شعبه',
  market: 'بازار',
  diff: 'اختلاف',
  month: 'ماه',
  branchColon: 'شعبه:',
  marketColon: 'بازار:',
  estimated: 'تخمینی',
  daysUnit: 'روز',
} as const;

export const MARKET_PULSE_TABLE_MONTH_TOOLTIP =
  'ماه تقویمی مورد مقایسه؛ برای ماه‌های کامل تیک، و برای ماه‌های ناقص نشانگر پوشش داده نمایش داده می‌شود.';

// ---------------------------------------------------------------------------
// داشبورد
// ---------------------------------------------------------------------------
export const MARKET_PULSE_DASHBOARD = {
  name: 'نبض بازار',
  hook: 'ببینید در کجای بازار ایستاده اید.',
  introBannerBeforeLink: 'برای آشنایی با نبض بازار،',
  introBannerLink: 'سند محصول',
  introBannerAfterLink: 'را مطالعه کنید.',
  clubShortkey: 'باشگاه مشتریان',
  relatedLinksTitle: 'لینک‌های مرتبط',
  relatedLinks: [
    { label: 'باشگاه مشتریان', href: '/dashboard/club' },
  ] as const,
  errorFetch: 'خطا در دریافت داده‌های نبض بازار',
  emptyNoData: 'برای این شعبه در بازه‌ی انتخاب‌شده داده‌ای یافت نشد.',
  dataCoverageTitle: 'نحوه محاسبه وقتی دادهٔ شعبه کامل نیست',
  dataCoverageDescription:
    'در ماه‌هایی که داده‌ها به‌صورت کامل دریافت نشده یا هنوز ماه به پایان نرسیده، می‌توانید فقط دادهٔ ثبت‌شده را ببینید یا شاخص‌ها را تا پایان ماه تخمین بزنید.',
  toggleRecorded: 'داده واقعی',
  toggleExtrapolate: 'تخمینی',
} as const;

// ---------------------------------------------------------------------------
// KPI
// ---------------------------------------------------------------------------
export const MARKET_PULSE_KPI_TITLE = 'نتایج مقایسه کسب کار شما با کسب کار های متصل بازار';

/** رنگ‌های ثابت برای رندر درست در اسکرین‌شات (html2canvas) */
export const MARKET_PULSE_SCREENSHOT_COLORS = {
  ahead: '#00A71E',
  behind: '#EF4444',
  warning: '#F59E0B',
  neutral: '#6B7280',
  progressTrack: '#D1D5DB',
} as const;

export const MARKET_PULSE_KPI = {
  title: MARKET_PULSE_KPI_TITLE,
  errorFetch: 'خطا در دریافت شاخص‌های کلیدی نبض بازار',
  emptyNoData: 'برای این شعبه در بازه‌ی انتخاب‌شده داده‌ای برای شاخص‌ها یافت نشد.',
  favoriteDescription: 'مقایسه رشد دوره‌ای چهار شاخص کلیدی شعبه با کل بازار',
  ahead: 'جلوتر',
  behind: 'عقب‌تر',
  diffTitleSuffix: 'اختلاف رشد شعبه نسبت به بازار',
  comparisonBadge: 'شما ↔ بازار',
  youLabel: 'شما',
  estimationNoticePrefix: 'حالت',
  estimationNoticeSuffix: 'روشن است — برای ماه‌های ناقص، مقدار تا پایان ماه تکمیل شده است.',
  noticePanelTitle: 'وضعیت تکمیل داده های شعبه',
  noticeComparisonMonths: 'ماه‌های مقایسه',
  noticeExcludedMonths: 'خارج از این مقایسه',
  noticeIncomplete: 'ناقص',
  noticeComplete: 'کامل',
  noticeExcluded: 'کنار گذاشته',
  noticeExcludedCaption: 'در شاخص‌ها استفاده نشده',
  noticeBranchIncomplete: 'داده کامل نیست — شاخص ممکن است دقیق نباشد',
  noticeMarketComplete: 'داده کامل است',
  startMonth: 'ماه پایه',
  endMonth: 'ماه انتها',
  relativeTo: 'نسبت به',
  resetPeriod: 'بازنشانی',
  periodControlAria: 'انتخاب بازه مقایسه شاخص‌ها',
  startMonthPlaceholder: 'انتخاب ماه پایه',
  endMonthPlaceholder: 'انتخاب ماه انتها',
} as const;

/** برچسب گزینهٔ ماه در دراپ‌داون KPI با وضعیت پوشش داده */
export const formatKpiMonthOptionLabel = (
  monthLabel: string,
  orderDays: number,
  fullDays: number,
  incomplete: boolean,
): string =>
  incomplete
    ? `${monthLabel} · ${MARKET_PULSE_KPI.noticeIncomplete} (${orderDays}/${fullDays})`
    : `${monthLabel} · ${MARKET_PULSE_KPI.noticeComplete}`;

export const KPI_COMPARISON_ROLE_LABEL: Record<'period-start' | 'period-end', string> = {
  'period-start': 'ماه پایه · دادهٔ شعبه کامل نیست',
  'period-end': 'ماه انتها · دادهٔ شعبه کامل نیست',
};

// ---------------------------------------------------------------------------
// جدول
// ---------------------------------------------------------------------------
export const MARKET_PULSE_TABLE = {
  title: 'جدول مقایسه‌ی رشد ماهانه شعبه با  کسب‌وکارهای متصل بازار ',
  subtitle: 'رشد ماه‌به‌ماه هر شاخص — شعبه در برابر کل  کسب‌وکارهای متصل بازار ',
  favoriteDescription: 'مقایسه‌ی رشد ماه‌به‌ماه شاخص‌های کلیدی شعبه با کل  کسب‌وکارهای متصل بازار ',
  toggleShowMarket: 'با ستون بازار',
  toggleDiffOnly: 'فقط اختلاف',
  selectAtLeastOne: 'حداقل یک شاخص را انتخاب کنید.',
  scrollHint: 'برای مشاهده ستون‌های بیشتر، جدول را به چپ و راست بکشید',
  legendAhead: 'جلوتر از بازار',
  legendEqual: 'هم‌تراز بازار',
  legendBehind: 'عقب‌تر از بازار',
  legendEstimated: 'تخمین‌شده',
  legendBranchComplete: 'دادهٔ شعبه کامل است',
  legendBranchIncomplete: 'دادهٔ شعبه کامل نیست',
  legendUnreliablePair: 'شعبه و اختلاف — عدد تار؛ با آیکون چشم دیده می‌شود',
  legendMarketReliable: ' کسب‌وکارهای متصل بازار  قابل اتکا است',
  growthFormula: 'رشد = تغییر ماه‌به‌ماه · آستانه:',
} as const;

export const MARKET_PULSE_YOY_TABLE = {
  title: 'مقایسه رشد شعبه با ماه متناظر سال قبل و کسب‌وکارهای متصل بازار ',
  subtitle: 'رشد نسبت به ماه مشابه سال قبل — شعبه در برابر کل  کسب‌وکارهای متصل بازار ',
  favoriteDescription: 'مقایسه‌ی رشد سال‌به‌سال شاخص‌های کلیدی شعبه با کل  کسب‌وکارهای متصل بازار ',
  toggleShowMarket: 'با ستون بازار',
  toggleDiffOnly: 'فقط اختلاف',
  selectAtLeastOne: 'حداقل یک شاخص را انتخاب کنید.',
  scrollHint: 'برای مشاهده ستون‌های بیشتر، جدول را به چپ و راست بکشید',
  legendAhead: 'جلوتر از بازار',
  legendEqual: 'هم‌تراز بازار',
  legendBehind: 'عقب‌تر از بازار',
  legendEstimated: 'تخمین‌شده',
  legendBranchComplete: 'دادهٔ شعبه کامل است',
  legendBranchIncomplete: 'دادهٔ شعبه کامل نیست',
  legendUnreliablePair: 'شعبه و اختلاف — عدد تار؛ با آیکون چشم دیده می‌شود',
  legendMarketReliable: ' کسب‌وکارهای متصل بازار  قابل اتکا است',
  growthFormula: 'رشد = تغییر نسبت به ماه مشابه سال قبل · آستانه:',
  marketPairedNote: 'بازار: فقط شعب دارای داده در ماه جاری و ماه مشابه سال قبل (بیش از ۲۰ روز)',
} as const;

// ---------------------------------------------------------------------------
// نمودار روند
// ---------------------------------------------------------------------------
export const MARKET_PULSE_TREND = {
  title: 'روند مقایسه‌ای شعبه و  کسب‌وکارهای متصل بازار ',
  subtitle: 'مقادیر بر حسب درصد رشد ماه‌به‌ماه (%)',
  extrapolatedNote: 'ماه‌های با دادهٔ ناقص — تخمین تا پایان ماه',
  favoriteDescription: 'روند درصد رشد ماه‌به‌ماه شاخص انتخاب‌شده برای شعبه و کل  کسب‌وکارهای متصل بازار ',
  zeroLineNote:
    'خط‌چین افقی نشان‌دهنده‌ی مبنای صفر درصد است؛ مقادیر بالای آن رشد مثبت و پایین آن رشد منفی‌اند.',
} as const;

// ---------------------------------------------------------------------------
// پوشش داده
// ---------------------------------------------------------------------------
export const MARKET_PULSE_COVERAGE = {
  estimatedTitle: 'این ماه برای کل ماه تخمین زده شده است',
  limitedTitle: 'همه روزهای این ماه از سمت شعبه دریافت نشده است',
  estimatedBadge: 'تخمین‌شده',
  limitedBadge: 'داده محدود',
  completeTitle: 'دادهٔ این ماه از سمت شعبه کامل است',
  completeBadgeAria: 'ماه کامل',
  unreliableMetricTitle:
    'رشد شعبه و اختلاف این ماه ممکن است دقیق نباشد؛ دادهٔ این ماه یا ماه قبل هنوز کامل نشده. بازار قابل اتکا است.',
  unreliableYoYMetricTitle:
    'رشد شعبه و اختلاف این ماه ممکن است دقیق نباشد؛ دادهٔ این ماه یا ماه مشابه سال قبل هنوز کامل نشده. بازار قابل اتکا است.',
  blurRevealHint: 'برای مشاهده عدد، روی آیکون چشم کلیک کنید',
  blurHideHint: 'برای پنهان کردن عدد، دوباره روی آیکون چشم کلیک کنید',
  revealAllLabel: 'نمایش همه',
  hideAllLabel: 'پنهان کردن همه',
} as const;

// ---------------------------------------------------------------------------
// راهنمای نمودار (مودال)
// ---------------------------------------------------------------------------
export const MARKET_PULSE_GUIDE = {
  description: 'توضیحات',
  interpretation: 'نحوه تفسیر',
  tips: 'نکات کلیدی',
} as const;

// ---------------------------------------------------------------------------
// قالب‌های متنی
// ---------------------------------------------------------------------------
export const formatKpiComparisonMonthDetail = (
  monthLabel: string,
  orderDays: number,
  fullDays: number,
): string => `ماه ${monthLabel} — ${orderDays} از ${fullDays} روز داده دریافت شده`;

export const formatKpiExcludedMonthDetail = (
  monthLabel: string,
  orderDays: number,
  fullDays: number,
): string => `ماه ${monthLabel} — ${orderDays} از ${fullDays} روز — آخرین ماه دوره`;

export const formatTableDiffTitle = (diff: string): string =>
  `اختلاف شعبه با بازار: ${diff} واحد`;

export const formatKpiDiffCardTitle = (statusLabel: string): string =>
  `${statusLabel} — ${MARKET_PULSE_KPI.diffTitleSuffix}`;
